import React, { useState } from "react";
import {
  Stack,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import BarChartIcon from "@mui/icons-material/BarChart";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ListIcon from "@mui/icons-material/List";
import GroupsIcon from "@mui/icons-material/Groups";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

type ScheduleActionButtonsProps = {
  handleSessionAllocations?: () => void;
  handleLiveSessions?: () => void;
  handleSessionUsages?: () => void;
  handleAllocatedSessions?: () => void;
  handleCoHost?: () => void;
  handleObserver?: () => void;
  handleAttendanceSummary?: () => void;
  handleEdit?: () => void;
  handleDelete?: () => void;
  row?: any;
};

export const ScheduleActionButtons = (props: ScheduleActionButtonsProps) => {
  const {
    handleSessionAllocations,
    handleLiveSessions,
    handleSessionUsages,
    handleAllocatedSessions,
    handleCoHost,
    handleObserver,
    handleAttendanceSummary,
    handleEdit,
    handleDelete,
    row
  } = props;

  const isActionDisabled = row?.liveSession?.actualStartTime && new Date() > new Date(row?.liveSession?.actualStartTime);
  const scheduledStartTime = new Date(row?.liveSession?.scheduledStartTime).getTime();
  const isEditDeleteDisabled = Number.isFinite(scheduledStartTime) && scheduledStartTime <= Date.now();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (action?: () => void) => {
    handleMenuClose();
    action?.();
  };
  const actionButtonSx = {
    textTransform: "none",
    px: 1.5,
    width: 120,
    maxWidth: "70%",
    height: 42,
    minHeight: 42,
    whiteSpace: "normal",
    borderRadius: 2,
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        whiteSpace: "nowrap",
        overflowX: "auto",
        overflowY: "hidden",
      }}
    >
      <Button
        size="small"
        variant="contained"
        startIcon={<CalendarMonthIcon fontSize="small" />}
        sx={actionButtonSx}
        onClick={handleSessionAllocations}
        disabled={isActionDisabled}
      >
        Allocate
      </Button>

      <Button
        size="small"
        variant="outlined"
        startIcon={<ListIcon fontSize="small" />}
        sx={actionButtonSx}
        onClick={handleAllocatedSessions}
      >
        Allocated Sessions
      </Button>

      <Button
        size="small"
        variant="outlined"
        startIcon={<PlayArrowIcon fontSize="small" />}
        sx={actionButtonSx}
        onClick={handleLiveSessions}
      >
        Live Sessions
      </Button>

      <Tooltip title="More actions">
        <IconButton size="small" onClick={handleMenuOpen}>
          <MoreHorizIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{ sx: { width: 300, maxWidth: "100%" } }}
      >
        <MenuItem onClick={() => handleMenuItemClick(handleSessionUsages)}>
          <ListItemIcon>
            <BarChartIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Live session usage</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick(handleCoHost)}>
          <ListItemIcon>
            <GroupsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Co Host</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick(handleObserver)}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Observer</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick(() => handleAttendanceSummary?.())}>
          <ListItemIcon>
            <AssessmentOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Attendance Summary</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuItemClick(() => handleEdit?.())}
          disabled={isEditDeleteDisabled}
          sx={{ borderTop: 1, borderColor: "divider", color: "error.main" }}
        >
          <ListItemIcon>
            <EditOutlinedIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuItemClick(()=>handleDelete?.())}
          disabled={isEditDeleteDisabled}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Stack>
  );
};


type ScheduleCopyAction = {

  handleScheduleCopy?: () => void
};

export const ScheduleCopyAction = (props: ScheduleCopyAction
) => {
  const {
    handleScheduleCopy,
  } = props;

  return (
    <Tooltip title="Copy joining link">
      <IconButton size="small" onClick={handleScheduleCopy}>
        <ContentCopyIcon fontSize="small" sx={{ color: "black" }} />
      </IconButton>
    </Tooltip>
  );
};

type SessionInstancesActionProps = {
  handleSessionInstances?: () => void;
  row?: any;
};

export const SessionInstancesAction = (props: SessionInstancesActionProps) => {
  const { handleSessionInstances } = props;

  return (
    <Button
      size="small"
      variant="contained"
      sx={{ textTransform: "none", px: 1, whiteSpace: "nowrap" }}
      onClick={handleSessionInstances}
    >
      Session Instances
    </Button>
  );
};

type RecordingActionProps = {
  handleRecording?: () => void;
  row?: any;
};

export const RecordingAction = (props: RecordingActionProps) => {
  const { handleRecording, row } = props;

  const isRecordingUnavailable = !row?.metadata?.recording_metadata?.downloaded_url;

  return (
    <Button
      size="small"
      variant="outlined"
      sx={{ textTransform: "none", px: 1, whiteSpace: "nowrap" }}
      onClick={handleRecording}
      disabled={isRecordingUnavailable}
    >
      ▶︎ Play Recording
    </Button>
  );
};